/**
 * Pure sonar raycast — directional beams + bounce reflect (no circle marks).
 */
import { lobeAmp } from "../noise-lobe";

export type SonarHitKind =
  | "wall"
  | "loot_low"
  | "loot_high"
  | "exit"
  | "pursuer"
  | "player"
  /** Open air — ray radiates to max range with no opaque contact. */
  | "void";

export type SonarBeam = {
  ox: number;
  oy: number;
  hx: number;
  hy: number;
  /** Reflect end — bounce away from surface (need not return to player). */
  rx: number;
  ry: number;
  dist: number;
  amp: number;
  kind: SonarHitKind;
  theta: number;
  nx: number;
  ny: number;
  /** False when ray fades into open range (always-radiate miss). */
  hit: boolean;
};

export type SonarBlock = { x: number; y: number; w: number; h: number };
export type SonarLoot = {
  x: number;
  y: number;
  taken: boolean;
  tier: "low" | "high";
};

/** Opaque loot disc radius used by sonar casts (must match presentation circle). */
export const SONAR_LOOT_RADIUS = 14;

function rayHitAabb(
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  maxR: number,
  b: SonarBlock,
): { t: number; nx: number; ny: number } | null {
  const hw = b.w * 0.5;
  const hh = b.h * 0.5;
  const minX = b.x - hw;
  const maxX = b.x + hw;
  const minY = b.y - hh;
  const maxY = b.y + hh;
  let tMin = 0;
  let tMax = maxR;
  let nx = 0;
  let ny = 0;
  if (Math.abs(dx) < 1e-8) {
    if (ox < minX || ox > maxX) return null;
  } else {
    let t1 = (minX - ox) / dx;
    let t2 = (maxX - ox) / dx;
    let n1x = -1;
    let n2x = 1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      n1x = 1;
      n2x = -1;
    }
    if (t1 > tMin) {
      tMin = t1;
      nx = n1x;
      ny = 0;
    }
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return null;
  }
  if (Math.abs(dy) < 1e-8) {
    if (oy < minY || oy > maxY) return null;
  } else {
    let t1 = (minY - oy) / dy;
    let t2 = (maxY - oy) / dy;
    let n1y = -1;
    let n2y = 1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      n1y = 1;
      n2y = -1;
    }
    if (t1 > tMin) {
      tMin = t1;
      nx = 0;
      ny = n1y;
    }
    tMax = Math.min(tMax, t2);
    if (tMin > tMax) return null;
  }
  if (tMin < 0) {
    if (tMax < 0 || tMax > maxR) return null;
    return { t: tMax, nx, ny };
  }
  if (tMin > maxR) return null;
  if (nx === 0 && ny === 0) {
    // fell through without face update — pick nearest axis
    const hx = ox + dx * tMin;
    const hy = oy + dy * tMin;
    const dl = Math.abs(hx - minX);
    const dr = Math.abs(hx - maxX);
    const dt = Math.abs(hy - minY);
    const db = Math.abs(hy - maxY);
    const m = Math.min(dl, dr, dt, db);
    if (m === dl) {
      nx = -1;
      ny = 0;
    } else if (m === dr) {
      nx = 1;
      ny = 0;
    } else if (m === dt) {
      nx = 0;
      ny = -1;
    } else {
      nx = 0;
      ny = 1;
    }
  }
  return { t: tMin, nx, ny };
}

function rayHitCircle(
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  maxR: number,
  cx: number,
  cy: number,
  radius: number,
): { t: number; nx: number; ny: number } | null {
  const fx = ox - cx;
  const fy = oy - cy;
  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0 || a < 1e-10) return null;
  const s = Math.sqrt(disc);
  const t1 = (-b - s) / (2 * a);
  const t2 = (-b + s) / (2 * a);
  const t = t1 >= 0 ? t1 : t2;
  if (t < 0 || t > maxR) return null;
  const hx = ox + dx * t;
  const hy = oy + dy * t;
  let nx = hx - cx;
  let ny = hy - cy;
  const nl = Math.hypot(nx, ny) || 1;
  nx /= nl;
  ny /= nl;
  return { t, nx, ny };
}

function reflectDir(
  idx: number,
  idy: number,
  nx: number,
  ny: number,
): { x: number; y: number } {
  const dot = idx * nx + idy * ny;
  let rx = idx - 2 * dot * nx;
  let ry = idy - 2 * dot * ny;
  const rl = Math.hypot(rx, ry) || 1;
  rx /= rl;
  ry /= rl;
  return { x: rx, y: ry };
}

type OpaqueTargets = {
  blocks: readonly SonarBlock[];
  loot: readonly SonarLoot[];
  exit: { x: number; y: number; radius: number };
  pursuers?: readonly { x: number; y: number; radius: number }[] | null;
  player?: { x: number; y: number; radius: number } | null;
};

type OpaqueHit = {
  t: number;
  kind: SonarHitKind;
  nx: number;
  ny: number;
};

/** Nearest opaque along a unit direction within maxR (wall/loot/exit/actor). */
function nearestOpaqueHit(
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  maxR: number,
  targets: OpaqueTargets,
): OpaqueHit | null {
  let bestT = maxR;
  let kind: SonarHitKind = "wall";
  let nx = -dx;
  let ny = -dy;
  let found = false;

  for (const block of targets.blocks) {
    const hit = rayHitAabb(ox, oy, dx, dy, maxR, block);
    if (hit != null && hit.t < bestT) {
      bestT = hit.t;
      kind = "wall";
      nx = hit.nx;
      ny = hit.ny;
      found = true;
    }
  }
  for (const node of targets.loot) {
    if (node.taken) continue;
    const hit = rayHitCircle(
      ox,
      oy,
      dx,
      dy,
      maxR,
      node.x,
      node.y,
      SONAR_LOOT_RADIUS,
    );
    if (hit != null && hit.t < bestT) {
      bestT = hit.t;
      kind = node.tier === "high" ? "loot_high" : "loot_low";
      nx = hit.nx;
      ny = hit.ny;
      found = true;
    }
  }
  {
    const hit = rayHitCircle(
      ox,
      oy,
      dx,
      dy,
      maxR,
      targets.exit.x,
      targets.exit.y,
      targets.exit.radius,
    );
    if (hit != null && hit.t < bestT) {
      bestT = hit.t;
      kind = "exit";
      nx = hit.nx;
      ny = hit.ny;
      found = true;
    }
  }
  for (const pursuer of targets.pursuers ?? []) {
    const hit = rayHitCircle(
      ox,
      oy,
      dx,
      dy,
      maxR,
      pursuer.x,
      pursuer.y,
      pursuer.radius,
    );
    if (hit != null && hit.t < bestT) {
      bestT = hit.t;
      kind = "pursuer";
      nx = hit.nx;
      ny = hit.ny;
      found = true;
    }
  }
  if (targets.player) {
    const hit = rayHitCircle(
      ox,
      oy,
      dx,
      dy,
      maxR,
      targets.player.x,
      targets.player.y,
      targets.player.radius,
    );
    if (hit != null && hit.t < bestT) {
      bestT = hit.t;
      kind = "player";
      nx = hit.nx;
      ny = hit.ny;
      found = true;
    }
  }
  if (!found) return null;
  return { t: bestT, kind, nx, ny };
}

/**
 * Post-bounce path length from remaining lobe range (not hitDist×fraction stub).
 * Near hit → long leftover travel; far hit → short leftover.
 * Tip progress pairs this with remaining life so tip px/s stays constant.
 */
export function reflectPathLen(
  hitDist: number,
  lobeRange: number,
  minReflect = 18,
): number {
  const left = Math.max(0, lobeRange - Math.max(0, hitDist));
  return Math.max(minReflect, left);
}

/** Nudge off the hit face so secondary cast does not re-hit the same surface. */
const REFLECT_ORIGIN_EPS = 1.25;

/**
 * Clip bounce travel to first opaque along reflect dir (no free-flight through AABBs).
 * Single secondary hit — stop there (no multi-bounce chain).
 */
export function clipReflectEndpoint(
  hx: number,
  hy: number,
  bx: number,
  by: number,
  rLen: number,
  targets: OpaqueTargets,
): { rx: number; ry: number; clipped: boolean } {
  const budget = Math.max(0, rLen);
  if (budget < 1e-6) return { rx: hx, ry: hy, clipped: false };
  const ox = hx + bx * REFLECT_ORIGIN_EPS;
  const oy = hy + by * REFLECT_ORIGIN_EPS;
  const maxR = Math.max(0, budget - REFLECT_ORIGIN_EPS);
  const hit = nearestOpaqueHit(ox, oy, bx, by, maxR, targets);
  if (hit == null) {
    return { rx: hx + bx * budget, ry: hy + by * budget, clipped: false };
  }
  return {
    rx: ox + bx * hit.t,
    ry: oy + by * hit.t,
    clipped: true,
  };
}

/** Build one outbound (+ optional bounce) beam from origin along unit dir. */
function castOneRay(
  ox: number,
  oy: number,
  dx: number,
  dy: number,
  amp: number,
  range: number,
  theta: number,
  scatterIndex: number,
  targets: OpaqueTargets,
): SonarBeam {
  const primary = nearestOpaqueHit(ox, oy, dx, dy, range, targets);
  if (primary == null) {
    const hx = ox + dx * range;
    const hy = oy + dy * range;
    return {
      ox,
      oy,
      hx,
      hy,
      rx: hx,
      ry: hy,
      dist: range,
      amp,
      kind: "void",
      theta,
      nx: -dx,
      ny: -dy,
      hit: false,
    };
  }
  const { t: bestT, kind, nx, ny } = primary;
  const hx = ox + dx * bestT;
  const hy = oy + dy * bestT;
  const bounce = reflectDir(dx, dy, nx, ny);
  const scatter = (scatterIndex % 5) * 0.04 - 0.08;
  const cos = Math.cos(scatter);
  const sin = Math.sin(scatter);
  const bx = bounce.x * cos - bounce.y * sin;
  const by = bounce.x * sin + bounce.y * cos;
  const rLen = reflectPathLen(bestT, range);
  const clipped = clipReflectEndpoint(hx, hy, bx, by, rLen, targets);
  return {
    ox,
    oy,
    hx,
    hy,
    rx: clipped.rx,
    ry: clipped.ry,
    dist: bestT,
    amp,
    kind,
    theta,
    nx,
    ny,
    hit: true,
  };
}

export function castSonarPulse(args: {
  ox: number;
  oy: number;
  facingX: number;
  facingY: number;
  sideAmp: number;
  forwardPower: number;
  scale: number;
  maxRange: number;
  rayCount: number;
  blocks: readonly SonarBlock[];
  loot: readonly SonarLoot[];
  exit: { x: number; y: number; radius: number };
  /** Player-cast targets — any tracker circle. */
  pursuers?: readonly { x: number; y: number; radius: number }[] | null;
  /** When casting from pursuer — detect player contact. */
  player?: { x: number; y: number; radius: number } | null;
}): SonarBeam[] {
  const fl = Math.hypot(args.facingX, args.facingY) || 1;
  const facingRad = Math.atan2(args.facingY / fl, args.facingX / fl);
  const targets: OpaqueTargets = {
    blocks: args.blocks,
    loot: args.loot,
    exit: args.exit,
    pursuers: args.pursuers,
    player: args.player,
  };
  const beams: SonarBeam[] = [];
  const n = Math.max(8, Math.floor(args.rayCount));
  for (let i = 0; i < n; i++) {
    const theta = (i / n) * Math.PI * 2;
    const amp =
      lobeAmp(theta, facingRad, args.sideAmp, args.forwardPower) * args.scale;
    if (amp < 0.05) continue;
    const range = args.maxRange * amp;
    const dx = Math.cos(theta);
    const dy = Math.sin(theta);
    beams.push(
      castOneRay(args.ox, args.oy, dx, dy, amp, range, theta, i, targets),
    );
  }
  return beams;
}

/**
 * Loot hold noise — emit from shell contact points on the outer circumference,
 * radiating outward into the world (not trapped inside the disc).
 * Caller must omit the emitting loot from `loot` targets (self-hit exclude).
 */
export function castLootRimPulse(args: {
  cx: number;
  cy: number;
  radius: number;
  scale: number;
  maxRange: number;
  rayCount: number;
  blocks: readonly SonarBlock[];
  loot: readonly SonarLoot[];
  exit: { x: number; y: number; radius: number };
  pursuers?: readonly { x: number; y: number; radius: number }[] | null;
  player?: { x: number; y: number; radius: number } | null;
}): SonarBeam[] {
  const targets: OpaqueTargets = {
    blocks: args.blocks,
    loot: args.loot,
    exit: args.exit,
    pursuers: args.pursuers,
    player: args.player,
  };
  const beams: SonarBeam[] = [];
  const n = Math.max(8, Math.floor(args.rayCount));
  const amp = args.scale;
  if (amp < 0.05) return beams;
  const range = args.maxRange * amp;
  const shellR = Math.max(1, args.radius) + REFLECT_ORIGIN_EPS;
  for (let i = 0; i < n; i++) {
    const theta = (i / n) * Math.PI * 2;
    const dx = Math.cos(theta);
    const dy = Math.sin(theta);
    const ox = args.cx + dx * shellR;
    const oy = args.cy + dy * shellR;
    beams.push(castOneRay(ox, oy, dx, dy, amp, range, theta, i, targets));
  }
  return beams;
}

/** Scale RGB channels toward aged memory paint (keeps hue of live rail). */
export function scaleRgb(color: number, mul: number): number {
  const m = Math.max(0, mul);
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * m));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * m));
  const b = Math.min(255, Math.round((color & 0xff) * m));
  return (r << 16) | (g << 8) | b;
}

/** Hard / soft memory strokes from a live rail (or reflect-tinted) color. */
export function memoryPaintFromRail(railColor: number): {
  hard: number;
  soft: number;
} {
  return {
    hard: scaleRgb(railColor, 0.64),
    soft: scaleRgb(railColor, 0.38),
  };
}

/** Channel-wise RGB multiply (0xRRGGBB × 0xRRGGBB). */
export function multiplyRgb(a: number, b: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.min(255, Math.round((ar * br) / 255)) << 16) |
    (Math.min(255, Math.round((ag * bg) / 255)) << 8) |
    Math.min(255, Math.round((ab * bb) / 255))
  );
}

/** Linear RGB blend — `t` is weight of `b` (0 = a, 1 = b). */
export function lerpRgb(a: number, b: number, t: number): number {
  const u = Math.max(0, Math.min(1, t));
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.min(255, Math.round(ar + (br - ar) * u)) << 16) |
    (Math.min(255, Math.round(ag + (bg - ag) * u)) << 8) |
    Math.min(255, Math.round(ab + (bb - ab) * u))
  );
}

/**
 * Loot bounce — weight of identity vs outbound light-blue base.
 * High so rarity hue reads on the reflect rail (multiply alone washed with pale bases).
 */
export const LOOT_REFLECT_BLEND = 0.75;

/** Loot / exit identity tint (reflect rail + contact — not filled discs). */
export function objectIdentityColor(kind: SonarHitKind): number | null {
  switch (kind) {
    case "loot_high":
      // Rich amber-gold — no pale/near-white wash
      return 0xc9920a;
    case "loot_low":
      // Steel silver-blue — chromatic, not slate-white
      return 0x5a7ea0;
    case "exit":
      return 0x4ade80;
    default:
      return null;
  }
}

/** Exit-hold isotropic rail (player-centered; not loot identity). */
export const HOLD_EXIT_RAIL_COLOR = 0x93c5fd;

/**
 * PlayerSonar outbound rail from event tint.
 * Loot hold → rarity identity; bounce `reflectRailColor` inherits this base.
 */
export function playerSonarRailColor(
  tint: "player" | "orange" | "hold" | "loot_low" | "loot_high" | "heartbeat",
  palette: { sonarOrange: number; sonarLightBlue: number },
): number {
  switch (tint) {
    case "orange":
      return palette.sonarOrange;
    case "hold":
      return HOLD_EXIT_RAIL_COLOR;
    case "loot_high":
      return objectIdentityColor("loot_high")!;
    case "loot_low":
      return objectIdentityColor("loot_low")!;
    case "heartbeat":
      // Same player-family blue; softness comes from scale/lifeMul, not a new hue.
      return scaleRgb(palette.sonarLightBlue, 0.72);
    default:
      return palette.sonarLightBlue;
  }
}

/**
 * Pursuer stride rail from mode snapshot on PursuerSonar.
 * search → low-chroma pursuer; investigate/sweep/chase/catching → danger red.
 */
export function pursuerSonarRailColor(
  mode: "search" | "investigate" | "sweep" | "chase" | "catching",
  palette: { sonarLightPursuer: number; sonarLightRed: number },
): number {
  return mode === "search" ? palette.sonarLightPursuer : palette.sonarLightRed;
}

/** Chase/catching residual life mul — search/investigate/sweep stay at 1. */
export function pursuerSonarLifeMul(
  mode: "search" | "investigate" | "sweep" | "chase" | "catching",
  chaseLifeMul: number,
): number {
  return mode === "chase" || mode === "catching"
    ? Math.max(1, chaseLifeMul)
    : 1;
}

/** Outbound rail — faction light; loot/exit identity lives on the reflect rail. */
export function beamColor(kind: SonarHitKind): number {
  switch (kind) {
    case "player":
      return 0xf87171;
    case "void":
      return 0x4f8fd0;
    default:
      return 0x60a5fa;
  }
}

/**
 * Returning / bounce rail — loot lerps hard toward identity; exit multiplies.
 * Walls / pursuer keep the outbound rail color.
 */
export function reflectRailColor(kind: SonarHitKind, base: number): number {
  const id = objectIdentityColor(kind);
  if (id == null) return base;
  if (kind === "loot_high" || kind === "loot_low") {
    return lerpRgb(base, id, LOOT_REFLECT_BLEND);
  }
  return multiplyRgb(base, id);
}

/**
 * Soft interior wash — pursuer/player threat only.
 * Loot/exit identity is read from reflect rail + edge stamp (no filled disc).
 */
export function objectInteriorColor(kind: SonarHitKind): number | null {
  switch (kind) {
    case "pursuer":
      return 0xb85c5c;
    case "player":
      return 0xb85c5c;
    default:
      return null;
  }
}

/**
 * Soft surface tint at the opaque contact edge.
 * Walls stay near outbound rail hue (soft crest, not specular flash).
 */
export function contactSurfaceColor(kind: SonarHitKind): number {
  switch (kind) {
    case "loot_high":
      return 0xe0a010;
    case "loot_low":
      // Match steel identity — was near-white (#cbd5e1) and washed contact
      return 0x6b8fb0;
    case "pursuer":
      return 0xef4444;
    case "player":
      return 0xf87171;
    case "exit":
      return 0x4ade80;
    case "void":
      return 0x000000;
    default:
      // Wall tip-kiss — same family as light-blue rail (wave crest, not hot flash)
      return 0x7dd3fc;
  }
}

/** Minimal stroke surface for AABB / circle perimeter bake (Phaser Graphics ok). */
export type OutlineStrokeTarget = {
  lineStyle(width: number, color: number, alpha?: number): void;
  lineBetween(x1: number, y1: number, x2: number, y2: number): void;
};

/**
 * Nearest solid AABB whose face is at the tip-kiss (for obstacle outline bake).
 * Prefers face matching hit normal; falls back to closest edge.
 */
export function pickSolidBlockAtHit(
  hx: number,
  hy: number,
  nx: number,
  ny: number,
  blocks: readonly SonarBlock[],
  eps = 6,
): SonarBlock | null {
  let best: SonarBlock | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  const nnx = nx;
  const nny = ny;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    const hw = b.w * 0.5;
    const hh = b.h * 0.5;
    const minX = b.x - hw;
    const maxX = b.x + hw;
    const minY = b.y - hh;
    const maxY = b.y + hh;
    const dx = Math.max(minX - hx, 0, hx - maxX);
    const dy = Math.max(minY - hy, 0, hy - maxY);
    const dist = Math.hypot(dx, dy);
    if (dist > eps) continue;
    // Face agreement: hit normal should point outward from that face
    let face = 0;
    if (Math.abs(nnx) >= Math.abs(nny)) {
      face = nnx < 0 ? Math.abs(hx - minX) : Math.abs(hx - maxX);
    } else {
      face = nny < 0 ? Math.abs(hy - minY) : Math.abs(hy - maxY);
    }
    const score = dist * 4 + face;
    if (score < bestScore) {
      bestScore = score;
      best = b;
    }
  }
  return best;
}

/**
 * Local tip-kiss outline window — NOT full obstacle perimeter.
 * Soft falloff along the rim away from contact (memory RT residual OK).
 */
export const OUTLINE_LOCAL_HALF_LEN = 34;
/** Circle arc half-angle (rad) around hit — ~±36°. */
export const OUTLINE_LOCAL_HALF_ANGLE = Math.PI / 5;
export const OUTLINE_LOCAL_SOFT_ALPHA = 0.18;
export const OUTLINE_LOCAL_HARD_ALPHA = 0.3;

type PeriPoint = { x: number; y: number };

/** Closest point + arc-length param on AABB perimeter (CW from top-left). */
export function closestOnAabbPerimeter(
  hx: number,
  hy: number,
  block: SonarBlock,
): { x: number; y: number; s: number; peri: number } {
  const hw = block.w * 0.5;
  const hh = block.h * 0.5;
  const x0 = block.x - hw;
  const y0 = block.y - hh;
  const x1 = block.x + hw;
  const y1 = block.y + hh;
  const w = Math.max(1e-6, block.w);
  const h = Math.max(1e-6, block.h);
  const peri = 2 * (w + h);
  // Clamp to box, then push to nearest face
  const cx = Math.max(x0, Math.min(x1, hx));
  const cy = Math.max(y0, Math.min(y1, hy));
  const dl = Math.abs(cx - x0);
  const dr = Math.abs(cx - x1);
  const dt = Math.abs(cy - y0);
  const db = Math.abs(cy - y1);
  const m = Math.min(dl, dr, dt, db);
  let px = cx;
  let py = cy;
  let s = 0;
  if (m === dt) {
    px = cx;
    py = y0;
    s = Math.max(0, Math.min(w, px - x0));
  } else if (m === dr) {
    px = x1;
    py = cy;
    s = w + Math.max(0, Math.min(h, py - y0));
  } else if (m === db) {
    px = cx;
    py = y1;
    s = w + h + Math.max(0, Math.min(w, x1 - px));
  } else {
    px = x0;
    py = cy;
    s = w + h + w + Math.max(0, Math.min(h, y1 - py));
  }
  return { x: px, y: py, s, peri };
}

function aabbPeriPoint(block: SonarBlock, sRaw: number): PeriPoint {
  const hw = block.w * 0.5;
  const hh = block.h * 0.5;
  const x0 = block.x - hw;
  const y0 = block.y - hh;
  const x1 = block.x + hw;
  const y1 = block.y + hh;
  const w = Math.max(1e-6, block.w);
  const h = Math.max(1e-6, block.h);
  const peri = 2 * (w + h);
  let s = ((sRaw % peri) + peri) % peri;
  if (s <= w) return { x: x0 + s, y: y0 };
  s -= w;
  if (s <= h) return { x: x1, y: y0 + s };
  s -= h;
  if (s <= w) return { x: x1 - s, y: y1 };
  s -= w;
  return { x: x0, y: y1 - s };
}

function outlineFalloff(u: number): number {
  // Smoothstep-ish: 1 at contact, 0 at window edge
  const t = Math.max(0, Math.min(1, 1 - Math.abs(u)));
  return t * t * (3 - 2 * t);
}

/**
 * Stroke a short AABB perimeter chain centered on tip-kiss, alpha falloff from hit.
 * Does not stroke the full box.
 */
export function strokeAabbLocalOutline(
  gfx: OutlineStrokeTarget,
  block: SonarBlock,
  hx: number,
  hy: number,
  color: number,
  peakAlpha: number,
  width: number,
  halfLen = OUTLINE_LOCAL_HALF_LEN,
  steps = 10,
): void {
  const hit = closestOnAabbPerimeter(hx, hy, block);
  const half = Math.max(4, halfLen);
  const n = Math.max(4, Math.floor(steps));
  let prev = aabbPeriPoint(block, hit.s - half);
  for (let i = 1; i <= n; i++) {
    const u0 = (i - 1) / n;
    const u1 = i / n;
    const s1 = hit.s - half + u1 * 2 * half;
    const midU = (u0 + u1) * 0.5 * 2 - 1; // [-1,1]
    const a = peakAlpha * outlineFalloff(midU);
    const cur = aabbPeriPoint(block, s1);
    if (a > 0.01) {
      gfx.lineStyle(width, color, a);
      gfx.lineBetween(prev.x, prev.y, cur.x, cur.y);
    }
    prev = cur;
  }
}

/**
 * Stroke a local arc on a circle perimeter around the tip-kiss (not full ring).
 */
export function strokeCircleLocalOutline(
  gfx: OutlineStrokeTarget,
  cx: number,
  cy: number,
  radius: number,
  hx: number,
  hy: number,
  color: number,
  peakAlpha: number,
  width: number,
  halfAngle = OUTLINE_LOCAL_HALF_ANGLE,
  segments = 12,
): void {
  const r = Math.max(1, radius);
  const hitAng = Math.atan2(hy - cy, hx - cx);
  const half = Math.max(0.08, halfAngle);
  const n = Math.max(4, Math.floor(segments));
  let px = cx + Math.cos(hitAng - half) * r;
  let py = cy + Math.sin(hitAng - half) * r;
  for (let i = 1; i <= n; i++) {
    const u0 = (i - 1) / n;
    const u1 = i / n;
    const midU = (u0 + u1) * 0.5 * 2 - 1;
    const a = peakAlpha * outlineFalloff(midU);
    const ang = hitAng - half + u1 * 2 * half;
    const x = cx + Math.cos(ang) * r;
    const y = cy + Math.sin(ang) * r;
    if (a > 0.01) {
      gfx.lineStyle(width, color, a);
      gfx.lineBetween(px, py, x, y);
    }
    px = x;
    py = y;
  }
}

/** Tip-kiss kinds that bake obstacle outline into memory RT (no live thick stamp). */
export function tipKissUsesObstacleOutline(kind: SonarHitKind): boolean {
  return (
    kind === "wall" ||
    kind === "loot_low" ||
    kind === "loot_high" ||
    kind === "exit"
  );
}

/**
 * Pure outline bake plan for tip-kiss → memory RT.
 * Local perimeter window at hit (not full AABB/circle stroke).
 * Multi-rail: wall uses `railColor` (blue / red / orange / loot identity);
 * loot/exit discs use object identity (not the inbound rail).
 */
export type ObstacleOutlinePlan =
  | {
      shape: "aabb";
      key: string;
      color: number;
      block: SonarBlock;
      hx: number;
      hy: number;
      halfLen: number;
      wSoft: number;
      wHard: number;
      aSoft: number;
      aHard: number;
    }
  | {
      shape: "circle";
      key: string;
      color: number;
      cx: number;
      cy: number;
      radius: number;
      hx: number;
      hy: number;
      halfAngle: number;
      wSoft: number;
      aSoft: number;
      aHard: number;
    };

export function planObstacleOutlineStamp(args: {
  kind: SonarHitKind;
  hx: number;
  hy: number;
  nx: number;
  ny: number;
  amp: number;
  /** Live beam rail (player blue / pursuer red / scrape orange / loot hold). */
  railColor: number;
  blocks: readonly SonarBlock[];
  extractRadius: number;
  lootRadius?: number;
}): ObstacleOutlinePlan | null {
  if (!tipKissUsesObstacleOutline(args.kind)) return null;
  const amp = Math.max(0, args.amp);
  // Subtle vs old full-outline weights
  const wSoft = 0.85 + amp * 0.25;
  const wHard = 0.5 + amp * 0.15;
  const aSoft = OUTLINE_LOCAL_SOFT_ALPHA;
  const aHard = OUTLINE_LOCAL_HARD_ALPHA;
  if (args.kind === "wall") {
    const block = pickSolidBlockAtHit(
      args.hx,
      args.hy,
      args.nx,
      args.ny,
      args.blocks,
    );
    if (block == null) return null;
    const color = args.railColor;
    const hit = closestOnAabbPerimeter(args.hx, args.hy, block);
    // Quantize locus so distant hits on same wall aren't cooled by one stamp
    const qx = Math.round(hit.x / 24);
    const qy = Math.round(hit.y / 24);
    return {
      shape: "aabb",
      key: `w:${block.x},${block.y},${block.w},${block.h}:${color}:${qx},${qy}`,
      color,
      block,
      hx: hit.x,
      hy: hit.y,
      halfLen: OUTLINE_LOCAL_HALF_LEN,
      wSoft,
      wHard,
      aSoft,
      aHard,
    };
  }
  const nl = Math.hypot(args.nx, args.ny) || 1;
  if (args.kind === "loot_low" || args.kind === "loot_high") {
    const r = args.lootRadius ?? SONAR_LOOT_RADIUS;
    const cx = args.hx - (args.nx / nl) * r;
    const cy = args.hy - (args.ny / nl) * r;
    const color = objectIdentityColor(args.kind) ?? args.railColor;
    const qx = Math.round(args.hx / 16);
    const qy = Math.round(args.hy / 16);
    return {
      shape: "circle",
      key: `l:${Math.round(cx / 8)},${Math.round(cy / 8)},${args.kind}:${color}:${qx},${qy}`,
      color,
      cx,
      cy,
      radius: r,
      hx: args.hx,
      hy: args.hy,
      halfAngle: OUTLINE_LOCAL_HALF_ANGLE,
      wSoft,
      aSoft,
      aHard,
    };
  }
  if (args.kind === "exit") {
    const r = args.extractRadius;
    const cx = args.hx - (args.nx / nl) * r;
    const cy = args.hy - (args.ny / nl) * r;
    const color = objectIdentityColor("exit") ?? 0x4ade80;
    const qx = Math.round(args.hx / 16);
    const qy = Math.round(args.hy / 16);
    return {
      shape: "circle",
      key: `e:${Math.round(cx / 8)},${Math.round(cy / 8)}:${color}:${qx},${qy}`,
      color,
      cx,
      cy,
      radius: r,
      hx: args.hx,
      hy: args.hy,
      halfAngle: OUTLINE_LOCAL_HALF_ANGLE,
      wSoft,
      aSoft,
      aHard,
    };
  }
  return null;
}
